import {computed} from 'mobx';
import {DashboardState} from '../../../../states/dashboard/_contracts/DashboardState';
import tsDate from '@techsee/techsee-date';

export interface ITitleData {
    text: string;
    selectedCount?: number;
    showImageLoader?: boolean;
}

export interface IEmptyStateData {
    isEmpty: boolean;
    emptyText?: string;
}

export interface IOpenButtonData {
    show: boolean;
    text: string;
    onClick: () => void;
    textAlign?: 'left' | 'right';
}

export interface IResourceGalleryDependencies {
    translate: (key: string) => string;
    getLocale: () => string;
    useNewInterface: boolean;
    dir: 'ltr' | 'rtl';
    toggleImageSection: () => void;
    toggleHistSection: () => void;
    getSelectedResourcesCount: (type: 'images' | 'history') => number;
    isSelfServiceResource: (resource: any) => boolean;
    getBadgeValue: (resource: any) => string | number;
}

export interface IResourceGalleryController {
    readonly translate: (key: string) => string;
    readonly useNewInterface: boolean;
    readonly dir: 'ltr' | 'rtl';
    readonly className: string;
    readonly resourceType: 'images' | 'history';
    readonly isHistorySection: boolean;
    sectionOpen: boolean;
    shouldShow: boolean;
    isEmpty: boolean;
    toggleSection(): void;
    getTitle(): ITitleData;
    getEmptyStateContent(): IEmptyStateData | null;
    getResources(): any[];
    shouldDisplayBadge(resource: any, index: number, recordIndex?: number): boolean;
    getBadgeValue(resource: any, index: number, recordIndex?: number): string;
    getInteractionSummaries(): any[];
    getOpenButton(): IOpenButtonData | null;
    shouldGroupByDate(): boolean;
    formatTimestamp(timestamp: string): string;
    hasVideos(): boolean;
    getVideosForRecord(record: any): any[];
    getShowMultipleResourcesShare(): boolean;
    getSelectedCount(): number;
    getShowImageLoader(): boolean;
    getShowToggleIcon(): boolean;
}

export class ImagesGalleryController implements IResourceGalleryController {
    public readonly translate: (key: string) => string;
    public readonly useNewInterface: boolean;
    public readonly dir: 'ltr' | 'rtl';
    public readonly className: string = 'images-section';
    public readonly resourceType: 'images' | 'history' = 'images';
    public readonly isHistorySection: boolean = false;

    private readonly _deps: IResourceGalleryDependencies;
    private readonly _dashboardState: DashboardState;

    constructor(deps: IResourceGalleryDependencies, dashboardState: DashboardState) {
        this._deps = deps;
        this._dashboardState = dashboardState;
        this.translate = deps.translate;
        this.useNewInterface = deps.useNewInterface;
        this.dir = deps.dir;
    }

    @computed
    get sectionOpen(): boolean {
        return this._dashboardState.rightBarState.imageSectionOpen;
    }

    @computed
    get shouldShow(): boolean {
        return !this._dashboardState.rightBarState.libraryTaggingMode;
    }

    @computed
    get isEmpty(): boolean {
        return this._dashboardState.screenshots.length === 0;
    }

    toggleSection(): void {
        this._deps.toggleImageSection();
    }

    getShowToggleIcon(): boolean {
        const observe = this._dashboardState.isObserve;
        const offlineMode = this._dashboardState.isOfflineMode || this._dashboardState.offlineMode;
        const clientDisconnected = !this._dashboardState.clientRoomStorage.connected;

        return !this.getShowImageLoader() || observe || offlineMode || clientDisconnected;
    }

    getTitle(): ITitleData {
        const selectedCount = this._deps.getSelectedResourcesCount('images');
        const allowUpload = this._dashboardState.rightBarState.allowUploadImageFileFromDashboard;
        const observe = this._dashboardState.isObserve;
        const offlineMode = this._dashboardState.isOfflineMode || this._dashboardState.offlineMode;
        const clientDisconnected = !this._dashboardState.clientRoomStorage.connected;
        const showImageLoader = allowUpload && !observe && !offlineMode && !clientDisconnected;
        const multipleImagesSharingEnabled = this._dashboardState.multipleImagesSharingEnabled;
        const useNewInterface = this.useNewInterface;

        return {
            text: 'MAIN.VIEW.IMAGES',
            selectedCount:
                multipleImagesSharingEnabled && useNewInterface && selectedCount > 0 ? selectedCount : undefined,
            showImageLoader: showImageLoader || undefined
        };
    }

    getEmptyStateContent(): IEmptyStateData | null {
        if (!this.isEmpty) {
            return null;
        }
        return {
            isEmpty: true,
            emptyText: 'MAIN.VIEW.NO_IMAGES_YET'
        };
    }

    getResources(): any[] {
        return [...this._dashboardState.screenshots].reverse();
    }

    shouldDisplayBadge(resource: any, index: number): boolean {
        return this._deps.isSelfServiceResource(resource) || (resource.resourceIndex ?? 0) > 0;
    }

    getBadgeValue(resource: any, index: number): string {
        return String(this._deps.getBadgeValue(resource));
    }

    getInteractionSummaries(): any[] {
        return [];
    }

    getOpenButton(): IOpenButtonData | null {
        return null;
    }

    shouldGroupByDate(): boolean {
        return false;
    }

    formatTimestamp(timestamp: string): string {
        return '';
    }

    hasVideos(): boolean {
        return false;
    }

    getVideosForRecord(record: any): any[] {
        return [];
    }

    getShowMultipleResourcesShare(): boolean {
        return this._dashboardState.showMultipleResourcesShareForImages;
    }

    getSelectedCount(): number {
        return this._deps.getSelectedResourcesCount('images');
    }

    getShowImageLoader(): boolean {
        const allowUpload = this._dashboardState.rightBarState.allowUploadImageFileFromDashboard;
        const observe = this._dashboardState.isObserve;
        const offlineMode = this._dashboardState.isOfflineMode || this._dashboardState.offlineMode;
        const clientDisconnected = !this._dashboardState.clientRoomStorage.connected;

        return allowUpload && !observe && !offlineMode && !clientDisconnected;
    }
}

export class HistoryGalleryController implements IResourceGalleryController {
    public readonly translate: (key: string) => string;
    public readonly useNewInterface: boolean;
    public readonly dir: 'ltr' | 'rtl';
    public readonly className: string = 'history-list-group';
    public readonly resourceType: 'images' | 'history' = 'history';
    public readonly isHistorySection: boolean = true;

    private readonly _deps: IResourceGalleryDependencies;
    private readonly _dashboardState: DashboardState;

    constructor(deps: IResourceGalleryDependencies, dashboardState: DashboardState) {
        this._deps = deps;
        this._dashboardState = dashboardState;
        this.translate = deps.translate;
        this.useNewInterface = deps.useNewInterface;
        this.dir = deps.dir;
    }

    @computed
    get sectionOpen(): boolean {
        return this._dashboardState.rightBarState.histSectionOpen;
    }

    @computed
    private get hasInteractionSummaries(): boolean {
        return (this._dashboardState.rightBarState.interactionSummaryPreviews ?? []).length > 0;
    }

    @computed
    get shouldShow(): boolean {
        const historyEmpty = this._dashboardState.rightBarState.historyEmpty;
        const enableHistory = this._dashboardState.rightBarState.enableHistory;
        const hideHistory = this._dashboardState.rightBarState.hideHistory;
        const libraryTaggingMode = this._dashboardState.rightBarState.libraryTaggingMode;

        return enableHistory && !libraryTaggingMode && !hideHistory && (!historyEmpty || this.hasInteractionSummaries);
    }

    @computed
    get isEmpty(): boolean {
        return this._dashboardState.rightBarState.historyEmpty && !this.hasInteractionSummaries;
    }

    toggleSection(): void {
        this._deps.toggleHistSection();
    }

    getShowToggleIcon(): boolean {
        return true;
    }

    getTitle(): ITitleData {
        const selectedCount = this._deps.getSelectedResourcesCount('history');
        const multipleImagesSharingEnabled = this._dashboardState.multipleImagesSharingEnabled;
        const useNewInterface = this.useNewInterface;

        return {
            text: 'MAIN.VIEW.CUSTOMER_HISTORY',
            selectedCount:
                multipleImagesSharingEnabled && useNewInterface && selectedCount > 0 ? selectedCount : undefined
        };
    }

    getEmptyStateContent(): IEmptyStateData | null {
        if (!this.isEmpty) {
            return null;
        }
        return {
            isEmpty: true,
            emptyText: 'MAIN.VIEW.NO_HISTORY_FOR_CUSTOMER'
        };
    }

    getResources(): any[] {
        const customerHistory = this._dashboardState.rightBarState.customerHistory;
        return customerHistory?.records || [];
    }

    shouldDisplayBadge(resource: any, index: number, recordIndex?: number): boolean {
        return true;
    }

    getBadgeValue(resource: any, index: number, recordIndex?: number): string {
        return String(index + 1);
    }

    getInteractionSummaries(): any[] {
        return this._dashboardState.rightBarState.interactionSummaryPreviews || [];
    }

    getOpenButton(): IOpenButtonData | null {
        if (this.isEmpty) {
            return null;
        }
        return {
            show: true,
            text: 'MAIN.VIEW.HISTORY.OPEN',
            onClick: () => {},
            textAlign: this.dir === 'rtl' ? 'right' : 'left'
        };
    }

    shouldGroupByDate(): boolean {
        return true;
    }

    formatTimestamp(timestamp: string): string {
        const locale = this._deps.getLocale();
        return tsDate.withLocale(tsDate(timestamp), locale).format('MMMM D, YYYY h:mm A');
    }

    hasVideos(): boolean {
        const records = this.getResources();
        return records.some((record: any) => record.videos?.length > 0);
    }

    getVideosForRecord(record: any): any[] {
        return record.videos || [];
    }

    getShowMultipleResourcesShare(): boolean {
        return (
            !!this._dashboardState.multipleImagesSharingEnabled &&
            !!this._dashboardState.useNewInterface &&
            !this._dashboardState.rightBarState.historyEmpty &&
            !!this._dashboardState.enableSharingImagesContainer
        );
    }

    getSelectedCount(): number {
        return this._deps.getSelectedResourcesCount('history');
    }

    getShowImageLoader(): boolean {
        return false;
    }
}
